import {memo, useCallback, useRef, useState} from 'react';

import {findIndex} from 'lodash';
import {useSelector} from 'react-redux';

import {
  selectCurrentAddress,
  selectOrdersHistory,
  selectOrdersHistoryLoading,
  selectUserData,
} from '~/shared/store/selectors';
import {getLocalizationService} from '~/shared/services/localisationService';
import {trackEvent} from '~/shared/services/analytics';
import {useAutoFocus} from '~/shared/hooks';
import {DropdownContainer} from '~/shared/components/DropdownButton';
import {SkeletonLoader} from '~/shared/components/Loaders';
import {useIsMaxLargeMobile, useIsMinLargeTablet} from '~/shared/hooks/deviceInfo';
import {fetchReordersIfNeeded, handleOnPreviewClick, handleOnReorderClick} from '~/shared/utils/reorder/utils';

import {ReorderItemsContainer, OverlayGradientBackground} from '../dropdown.styled';
import NoReorders from '../NoReorders';
import ReorderList from '../ReorderList';

import ReorderLoaderComponent from './components/ReorderLoaderComponent';

const ReorderDropdown = ({className = ''}) => {
  const {t} = getLocalizationService();
  const userData = useSelector(selectUserData);
  const currentAddress = useSelector(selectCurrentAddress);
  const reorderItems = useSelector(selectOrdersHistory);
  const isReorderListLoading = useSelector(selectOrdersHistoryLoading);

  const isMinLargeTablet = useIsMinLargeTablet();
  const isMaxLargeMobile = useIsMaxLargeMobile();

  const lastScrollRef = useRef({});

  const [isOpen, setIsOpen] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [listRef, setListRef] = useState();

  useAutoFocus({
    domEl: listRef?.current,
    triggerCondition: !!listRef?.current,
  });

  const closeDropdown = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = useCallback(() => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    setIsOpen(true);
    trackEvent('hasOpenedReorderDropdown');
    fetchReordersIfNeeded({addressId: currentAddress.addressId});
  }, [currentAddress, isOpen]);

  const onListScroll = useCallback(e => {
    const el = e.currentTarget;
    setIsScrolledToBottom(el.scrollHeight - el.scrollTop === el.clientHeight);
    setIsScrolledToTop(!el.scrollTop || el.scrollTop < 20);
    lastScrollRef.current.lastScrollTop = el.scrollTop;
  }, []);

  const handlePreviewClick = ({orderId, restaurantId}) => {
    const orderPositionInList = findIndex(reorderItems, {orderId}) + 1;
    handleOnPreviewClick({orderId, restaurantId, orderPositionInList, callback: closeDropdown});
  };

  const handleReorderClick = ({orderId, restaurantId}) => {
    handleOnReorderClick({orderId, restaurantId, reorderItems});
  };

  if (!userData || !isMinLargeTablet) {
    return null;
  }

  const showNoReorders = (reorderItems.length <= 0 && !isReorderListLoading) || !currentAddress?.addressId;
  const isReorderItemsLengthValidForOverlays = reorderItems?.length > 4;

  return (
    <DropdownContainer
      className={className}
      onDropdownClick={toggleDropdown}
      closeDropdown={closeDropdown}
      isOpen={isOpen}
      btnId="reorder-dropdown"
      btnAriaLabel={isOpen ? t('close_reorder_menu') : t('open_reorder_menu')}
      showArrow
      buttonTitle={t('reorder')}
      isMaxLargeMobile={isMaxLargeMobile}
    >
      {isOpen && (
        <ReorderItemsContainer>
          <SkeletonLoader
            shouldShowLoader={Boolean(currentAddress?.addressId) && isReorderListLoading}
            LoaderComponent={ReorderLoaderComponent}
          >
            {showNoReorders ? (
              <NoReorders />
            ) : (
              <>
                {isReorderItemsLengthValidForOverlays && <OverlayGradientBackground show={!isScrolledToTop} />}

                <ReorderList
                  handleSetListRef={setListRef}
                  items={reorderItems}
                  handleListScroll={onListScroll}
                  handleOnPreviewClick={handlePreviewClick}
                  handleOnReorderClick={handleReorderClick}
                />
                {isReorderItemsLengthValidForOverlays && (
                  <OverlayGradientBackground bottom show={!isScrolledToBottom} />
                )}
              </>
            )}
          </SkeletonLoader>
        </ReorderItemsContainer>
      )}
    </DropdownContainer>
  );
};

export default memo(ReorderDropdown);
